import { DropdownMenuPosition } from '../interface';

const MENU_MARGIN = 4; // xxs

function findFocusedIndex(menuItems: HTMLElement[]) {
  const focusedElement = document.activeElement;
  const index = menuItems.findIndex((item) => item === focusedElement);
  return index;
}

export function handlePrevOrNextItemFocus(
  focusIndex: 1 | -1,
  containerDomRef: HTMLElement | undefined | null,
  itemSelector: string
): void {
  const menuItems: NodeListOf<HTMLElement> | undefined =
    containerDomRef?.querySelectorAll?.(itemSelector);

  if (!menuItems?.length) {
    return;
  }

  const currentFocusIndex = findFocusedIndex(Array.from(menuItems));

  if (focusIndex === 1) {
    if (currentFocusIndex < menuItems.length - 1) {
      menuItems[currentFocusIndex + 1].focus();
    } else {
      menuItems[0].focus();
    }
  } else if (focusIndex === -1) {
    if (currentFocusIndex > 0) {
      menuItems[currentFocusIndex - 1].focus();
    } else {
      menuItems[menuItems.length - 1].focus();
    }
  }
}

export function getPositionCoordinates(
  position: DropdownMenuPosition,
  anchorRect: DOMRect,
  containerDomRect: DOMRect
): {
  left: number;
  top: number;
  maxHeight: number;
  maxWidth: number;
} {
  const {
    top: anchorTop,
    bottom: anchorBottom,
    right: anchorRight,
    left: anchorLeft,
    width: anchorWidth
  } = anchorRect;

  const { width: containerWidth, height: containerHeight } = containerDomRect;

  const screenHeight: number = window.innerHeight;
  const screenWidth: number = window.innerWidth;

  let left = 0;
  let top = 0;
  let maxHeight;
  let maxWidth;
  switch (position) {
    case 'bottom-start':
      left = anchorLeft;
      top = anchorBottom;
      maxHeight = screenHeight - anchorBottom;
      maxWidth = screenWidth - anchorLeft;
      break;
    case 'bottom-end':
      left = anchorRight - containerWidth;
      top = anchorBottom;
      maxHeight = screenHeight - anchorBottom;
      maxWidth = anchorRight;
      break;
    case 'top-start':
      left = anchorLeft;
      top = anchorTop - containerHeight;
      maxHeight = anchorTop;
      maxWidth = screenWidth - anchorLeft;
      break;
    case 'top-end':
      left = anchorRight - containerWidth;
      top = anchorTop - containerHeight;
      maxHeight = anchorTop;
      maxWidth = anchorRight;
      break;
    case 'right-end':
      left = anchorRight;
      top = anchorBottom - containerHeight;
      maxHeight = anchorBottom;
      maxWidth = screenWidth - anchorRight;
      break;
    case 'right-start':
      left = anchorRight;
      top = anchorTop;
      maxHeight = screenHeight - anchorTop;
      maxWidth = screenWidth - anchorRight;
      break;
    case 'left-end':
      left = anchorLeft - containerWidth;
      top = anchorBottom - containerHeight;
      maxHeight = anchorBottom;
      maxWidth = anchorLeft;
      break;
    case 'left-start':
      left = anchorLeft - containerWidth;
      top = anchorTop;
      maxHeight = screenHeight - anchorTop;
      maxWidth = anchorLeft;
      break;
    case 'top-center':
      left = anchorLeft + anchorWidth / 2 - containerWidth / 2;
      top = anchorTop - containerHeight;
      maxHeight = anchorTop;
      maxWidth = screenWidth;
      break;
    case 'bottom-center':
      left = anchorLeft + anchorWidth / 2 - containerWidth / 2;
      top = anchorBottom;
      maxHeight = screenHeight - anchorBottom;
      maxWidth = screenWidth;
      break;
  }

  if (position.includes('top')) {
    top -= MENU_MARGIN;
  } else if (position.includes('bottom')) {
    top += MENU_MARGIN;
  } else if (position.includes('right')) {
    left += MENU_MARGIN;
  } else if (position.includes('left')) {
    left -= MENU_MARGIN;
  }

  return {
    left: Math.round(left < 0 ? 0 : left),
    top: Math.round(top < 0 ? 0 : top),
    maxHeight,
    maxWidth
  };
}

export const spaceAvailabilityOrder: Record<
  DropdownMenuPosition,
  Array<DropdownMenuPosition>
> = {
  'bottom-start': [
    'bottom-end',
    'bottom-center',
    'top-start',
    'top-end',
    'top-center'
  ],
  'bottom-end': [
    'bottom-start',
    'bottom-center',
    'top-end',
    'top-start',
    'top-center'
  ],
  'top-start': [
    'top-end',
    'top-center',
    'bottom-start',
    'bottom-end',
    'bottom-center'
  ],
  'top-end': [
    'top-start',
    'top-center',
    'bottom-end',
    'bottom-start',
    'bottom-center'
  ],
  'right-end': ['right-start', 'left-end', 'left-start'],
  'right-start': [
    'right-end',
    'left-start',
    'left-end',
    'bottom-end',
    'bottom-center'
  ],
  'left-end': ['left-start', 'right-end', 'right-start'],
  'left-start': ['left-end', 'right-start', 'right-end'],
  'top-center': [
    'top-start',
    'top-end',
    'bottom-center',
    'bottom-start',
    'bottom-end'
  ],
  'bottom-center': [
    'bottom-start',
    'bottom-end',
    'top-center',
    'top-start',
    'top-end'
  ]
};

export function getCalculatedPosition(
  position: DropdownMenuPosition,
  anchorRect: DOMRect,
  containerDomRect: DOMRect | undefined
): DropdownMenuPosition {
  if (!(position && anchorRect && containerDomRect)) {
    return position;
  }

  if (checkSpaceAvailability(position, anchorRect, containerDomRect)) {
    return position;
  }

  let alternativePosition = spaceAvailabilityOrder[position].find(
    (alternativePosition) => {
      return checkSpaceAvailability(
        alternativePosition,
        anchorRect,
        containerDomRect
      );
    }
  );

  // worst case, is no space available find a position where maximum space is available comparitively
  if (!alternativePosition) {
    alternativePosition = checkBestAvailablePosition(anchorRect);
  }

  return alternativePosition || position;
}

// check space availability for edge case
function checkBestAvailablePosition(
  anchorRect: DOMRect
): DropdownMenuPosition | undefined {
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  const { bottom, top, left, right } = anchorRect;

  const positionWidthHeightRatios: Record<DropdownMenuPosition, number> = {
    'bottom-start': (windowWidth - left) * (windowHeight - bottom),
    'bottom-end': right * (windowHeight - bottom),
    'top-start': (windowWidth - left) * top,
    'top-end': right * top,
    'right-start': (windowWidth - right) * (windowHeight - top),
    'right-end': (windowWidth - right) * bottom,
    'left-start': left * (windowHeight - top),
    'left-end': left * bottom,
    'top-center': 0,
    'bottom-center': 0
  };

  let bestPosition: DropdownMenuPosition = 'bottom-start';
  let bestAvailableSpace = 0;

  for (const [position, availableSpace] of Object.entries(
    positionWidthHeightRatios
  )) {
    if (availableSpace > bestAvailableSpace) {
      bestPosition = position as DropdownMenuPosition;
      bestAvailableSpace = availableSpace;
    }
  }

  return bestPosition;
}

function checkSpaceAvailability(
  position: DropdownMenuPosition,
  anchorRect: DOMRect,
  containerDomRect: DOMRect
): boolean {
  const {
    width: anchorWidth,
    top: anchorTop,
    bottom: anchorBottom,
    right: anchorRight,
    left: anchorLeft
  } = anchorRect;

  const { width: containerWidth, height: containerHeight } = containerDomRect;

  const screenWidth: number = window.innerWidth;
  const screenHeight: number = window.innerHeight;

  const screenStart = 0;

  switch (position) {
    case 'bottom-start':
      return (
        anchorBottom + containerHeight < screenHeight &&
        anchorLeft + containerWidth < screenWidth
      );
    case 'bottom-end':
      return (
        anchorBottom + containerHeight < screenHeight &&
        anchorRight - containerWidth > screenStart
      );
    case 'top-start':
      return (
        anchorLeft + containerWidth < screenWidth &&
        anchorTop - containerHeight > 0
      );
    case 'top-end':
      return (
        anchorRight - containerWidth > screenStart &&
        anchorTop - containerHeight > 0
      );
    case 'right-end':
      return (
        anchorRight + containerWidth < screenWidth &&
        anchorBottom - containerHeight > 0
      );
    case 'right-start':
      return (
        anchorRight + containerWidth < screenWidth &&
        anchorTop + containerHeight < screenHeight
      );
    case 'left-end':
      return (
        anchorLeft - containerWidth > screenStart &&
        anchorBottom - containerHeight > 0
      );
    case 'left-start':
      return (
        anchorTop + containerHeight < screenHeight &&
        anchorLeft - containerWidth > screenStart
      );
    case 'top-center':
      return (
        anchorTop - containerHeight > 0 &&
        anchorLeft + anchorWidth / 2 - containerWidth / 2 > screenStart &&
        anchorLeft + anchorWidth / 2 + containerWidth / 2 < screenWidth
      );
    case 'bottom-center':
      return (
        anchorBottom + containerHeight < screenHeight &&
        anchorLeft + anchorWidth / 2 - containerWidth / 2 > screenStart &&
        anchorLeft + anchorWidth / 2 + containerWidth / 2 < screenWidth
      );
    default:
      return true;
  }
}
